/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

export interface ViewModel {
    name: string
}

// `never[]` rather than `unknown[]`: constructor parameters are contravariant, so
// only `never` keeps classes with typed constructors assignable here. The container
// never constructs the class itself — it only reads `.name`.
export type Constructor<T> = abstract new (...args: never[]) => T

// Factories with differing signatures share one key space, so their argument types
// are erased on the way in. Registration and lookup are paired by class name at
// runtime, which the type system cannot verify — hence the cast in getViewModel.
type ErasedFactory = (...args: never[]) => ViewModel

export class ViewModelContainer {
    private registry = new Map<string, ViewModel>()
    private factories = new Map<string, ErasedFactory>()

    // Register a ViewModel with its factory
    registerViewModel<T extends ViewModel>(viewModelClass: Constructor<T>, factory: (...args: never[]) => T): void {
        const factoryKey = viewModelClass.name
        if (this.factories.has(factoryKey)) {
            throw new Error(`ViewModelContainer: factory for ${factoryKey} already registered`)
        }
        this.factories.set(factoryKey, factory)
    }

    // Get or create ViewModel by class
    getViewModel<T extends ViewModel>(viewModelClass: Constructor<T>, ...args: unknown[]): T {
        const factoryKey = viewModelClass.name
        const instanceKey = this.getInstanceKey(viewModelClass, args)

        // hit
        if (this.registry.has(instanceKey)) {
            return this.registry.get(instanceKey) as T
        }

        // miss
        const factory = this.factories.get(factoryKey)
        if (!factory) {
            throw new Error(`ViewModelContainer: No factory registered for ${viewModelClass.name}`)
        }

        const instance = factory(...(args as never[])) as T
        this.registry.set(instanceKey, instance)
        return instance
    }

    removeViewModel(viewModelClass: Constructor<ViewModel>, ...args: unknown[]): void {
        const key = this.getInstanceKey(viewModelClass, args)
        if (this.registry.delete(key)) {
            if (process.env.NODE_ENV === "development") {
                console.log(`ViewModelContainer: ${key} removed`)
            }
        } else {
            if (process.env.NODE_ENV === "development") {
                console.log(`ViewModelContainer: no viewmodel found for ${key}`)
            }
        }
    }

    // Nothing calls this today: logout redirects via `window.location.href`, so
    // the page tears down and the registry goes with it. Kept for a client-side
    // logout, which would need to clear it explicitly.
    // biome-ignore lint/correctness/noUnusedPrivateClassMembers: see above
    private resetContainer() {
        this.registry.clear()
        this.registry = new Map<string, ViewModel>()
        if (process.env.NODE_ENV === "development") {
            console.log("ViewModelContainer: registry cleared")
        }
    }

    private getInstanceKey<T>(viewModelClass: Constructor<T>, args: unknown[]): string {
        return `${viewModelClass.name}::${args.join(":")}`
    }
}
